import { FORMAT, formatDateTime } from "Common/dateTimeUtils/format";
import { themeVariables } from "Components/theme";
import WelcomePanel from "Components/welcomePanel";
import classNames from "classnames";
import { Legend } from "devextreme-react/chart";
import { Aggregation, Behavior, Chart, Font, Label, Margin, Scale, Series, SliderHandle, SliderMarker } from "devextreme-react/range-selector";
import chunk from "lodash/chunk";
import get from "lodash/get";
import head from "lodash/head";
import isArray from "lodash/isArray";
import isNumber from "lodash/isNumber";
import keyBy from "lodash/keyBy";
import last from "lodash/last";
import map from "lodash/map";
import maxBy from "lodash/maxBy";
import minBy from "lodash/minBy";
import sortBy from "lodash/sortBy";
import sumBy from "lodash/sumBy";
import React, { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { TIMELINE_RANGE_SELECTOR_MODE } from "../constants";
import locale from "./timelineRangeSelector.locale";
import Styled from "./timelineRangeSelector.styled";
import appendDefs from "./utils/appendDefs";
import getSliderElements from "./utils/getSliderElements";
import getTransitionConstraints from "./utils/getTransitionConstraints";
import getTranslateParams from "./utils/getTranslateParams";
import setSliderMarkersShow from "./utils/setSliderMarkersShow";
import changeColorDefs from "./utils/changeColorDefs";
const ANIMATION_STEP_PX = 3;
const NORMALIZED_COUNT_MIN = 5;
const dPathInactiveMarkerRegexp = /(?<startPart>.* L )(?<x1>\d+)(?<secondPart> \d+ L )(?<x2>\d+)(?<lastPart> \d+ L \d+ \d+ Z)/;
function TimelineRangeSelector(props) {
    const rangeSelectorRef = useRef(null);
    const playDataIndexRef = useRef(0);
    const rafId = useRef(0);
    const defsRef = useRef(null);
    const transitionConstraintsRef = useRef({
        minX: 0,
        maxX: 0,
        y: 0,
        width: 0
    });
    useEffect(() => () => {
        rafId.current && cancelAnimationFrame(rafId.current);
        props.onSetInterval && props.onSetInterval(undefined);
        props.onSetInPlayProgress && props.onSetInPlayProgress(false);
        props.onSetPresentationState && props.onSetPresentationState("stop");
    }, []);
    useEffect(() => {
        if (rangeSelectorRef.current)
            transitionConstraintsRef.current = getTransitionConstraints(rangeSelectorRef);
    }, []);
    const data = useMemo(() => {
        var _a, _b, _c, _d, _e, _f;
        if (!props.data || !props.data.length || !props.maxTicksCount)
            return undefined;
        let resultSorted = [];
        const dataSorted = sortBy(props.data, "Timestamp");
        let minTimestamp = (_a = head(dataSorted)) === null || _a === void 0 ? void 0 : _a.Timestamp;
        let maxTimestamp = (_b = last(dataSorted)) === null || _b === void 0 ? void 0 : _b.Timestamp;
        if (props.mode === TIMELINE_RANGE_SELECTOR_MODE.PRESENTATION) {
            const denominator = Math.ceil(dataSorted.length / props.maxTicksCount) || 1;
            resultSorted =
                denominator > 1
                    ? map(chunk(dataSorted, denominator), e => {
                        var _a;
                        return ({
                            Timestamp: (_a = last(e)) === null || _a === void 0 ? void 0 : _a.Timestamp,
                            Count: sumBy(e, "Count")
                        });
                    })
                    : dataSorted;
        }
        else {
            resultSorted = dataSorted;
        }
        const minCount = (_c = minBy(resultSorted, "Count")) === null || _c === void 0 ? void 0 : _c.Count;
        const maxCount = (_d = maxBy(resultSorted, "Count")) === null || _d === void 0 ? void 0 : _d.Count;
        resultSorted = map(resultSorted, r => ({
            ...r,
            NormalizedCount: maxCount - minCount > 0
                ? ((r.Count - minCount) * (100 - NORMALIZED_COUNT_MIN)) / (maxCount - minCount) +
                    NORMALIZED_COUNT_MIN
                : r.Count
        }));
        const countsMap = keyBy(resultSorted, "Timestamp");
        let aggregationInterval = 1;
        if (props.mode === TIMELINE_RANGE_SELECTOR_MODE.TIMELINE) {
            aggregationInterval = Math.max(Math.floor((maxTimestamp - minTimestamp) / props.maxTicksCount), 1);
            if (minTimestamp % aggregationInterval > 0) {
                minTimestamp -= minTimestamp % aggregationInterval;
                resultSorted.splice(0, 0, {
                    Timestamp: minTimestamp,
                    Count: 0,
                    NormalizedCount: 0
                });
            }
            maxTimestamp = maxTimestamp - (maxTimestamp % aggregationInterval) + aggregationInterval;
            resultSorted.push({
                Timestamp: maxTimestamp,
                Count: 0,
                NormalizedCount: 0
            });
        }
        return {
            sorted: resultSorted,
            sortedMinTimestamp: (_e = head(resultSorted)) === null || _e === void 0 ? void 0 : _e.Timestamp,
            sortedMaxTimestamp: (_f = last(resultSorted)) === null || _f === void 0 ? void 0 : _f.Timestamp,
            minTimestamp,
            maxTimestamp,
            countsMap,
            aggregationInterval
        };
    }, [props.data, props.maxTicksCount, props.mode]);
    useEffect(() => {
        var _a;
        (_a = rangeSelectorRef.current) === null || _a === void 0 ? void 0 : _a.instance.setValue([undefined, undefined]);
        props.onSetInterval && props.onSetInterval(undefined);
        props.onSetInPlayProgress && props.onSetInPlayProgress(false);
        props.onSetPresentationState && props.onSetPresentationState("stop");
    }, [data]);
    const timestampCategories = useMemo(() => props.mode === TIMELINE_RANGE_SELECTOR_MODE.PRESENTATION
        ? map(data === null || data === void 0 ? void 0 : data.sorted, "Timestamp")
        : undefined, [data, props.mode]);
    const customizeDateTimeText = useCallback((e) => {
        const ms = Number.parseInt(e.value || "0") || 0;
        return ms ? formatDateTime(ms, `${FORMAT.SHORT_DATE} ${FORMAT.TIME}`) : "";
    }, [props.mode]);
    const customizeCountText = useCallback((e) => {
        var _a;
        if (!data)
            return "";
        const timestamp = Number.parseInt(e.value || "0");
        if (!timestamp)
            return "";
        const count = (_a = data.countsMap[timestamp]) === null || _a === void 0 ? void 0 : _a.Count;
        return count ? `  ${count}  ` : "";
    }, [data]);
    const customizeTotalLabelText = useCallback((e) => {
        const pointData = get(e, "point.aggregationInfo.data");
        if (!isArray(pointData))
            return "";
        return `${sumBy(pointData, "Count")}`;
    }, []);
    const handlePlay = useCallback(() => {
        var _a;
        if (props.inPlayProgress || !data || !data.sorted.length || !((_a = rangeSelectorRef.current) === null || _a === void 0 ? void 0 : _a.instance))
            return;
        props.onSetInPlayProgress && props.onSetInPlayProgress(true);
        props.onSetPresentationState && props.onSetPresentationState("play");
        rangeSelectorRef.current.instance.setValue([
            data.sortedMinTimestamp,
            props.interval ? props.interval.to : data.sortedMaxTimestamp
        ]);
        const { rightSlider } = getSliderElements(rangeSelectorRef);
        if (!rightSlider)
            return;
        transitionConstraintsRef.current = getTransitionConstraints(rangeSelectorRef);
        if (!transitionConstraintsRef.current.width)
            return;
        const startX = props.interval
            ? getTranslateParams(rightSlider).x
            : transitionConstraintsRef.current.minX;
        let x = startX;
        playDataIndexRef.current = 0;
        setSliderMarkersShow(rangeSelectorRef, false);
        props.onSetInterval &&
            props.onSetInterval({
                from: data.minTimestamp,
                to: props.interval ? props.interval.to : data.sortedMinTimestamp
            });
        let oneTickWidthInPx = 0;
        const moveSlider = () => {
            var _a, _b, _c, _d;
            if (!rangeSelectorRef.current || !rightSlider.isConnected || !data.sorted) {
                rafId.current && cancelAnimationFrame(rafId.current);
                return;
            }
            x += ANIMATION_STEP_PX;
            oneTickWidthInPx = transitionConstraintsRef.current.width / data.sorted.length;
            if (x >= transitionConstraintsRef.current.maxX || !oneTickWidthInPx) {
                rafId.current && cancelAnimationFrame(rafId.current);
                rightSlider.setAttribute("transform", `translate(${transitionConstraintsRef.current.maxX},${transitionConstraintsRef.current.y})`);
                setSliderMarkersShow(rangeSelectorRef, true);
                props.onSetInterval && props.onSetInterval(undefined);
                (_a = rangeSelectorRef.current) === null || _a === void 0 ? void 0 : _a.instance.setValue([undefined, undefined]);
                props.onSetInPlayProgress && props.onSetInPlayProgress(false);
                props.onSetPresentationState && props.onSetPresentationState("pause");
                return;
            }
            rightSlider.setAttribute("transform", `translate(${x},${transitionConstraintsRef.current.y})`);
            const inactiveMarker = (_c = (_b = rangeSelectorRef.current) === null || _b === void 0 ? void 0 : _b.instance.element()) === null || _c === void 0 ? void 0 : _c.querySelector(".dxrs-slidersContainer > path");
            const prevDAttr = inactiveMarker === null || inactiveMarker === void 0 ? void 0 : inactiveMarker.getAttribute("d");
            if (inactiveMarker && prevDAttr) {
                const progressIndicatorDGroups = (_d = dPathInactiveMarkerRegexp.exec(prevDAttr)) === null || _d === void 0 ? void 0 : _d.groups;
                progressIndicatorDGroups &&
                    inactiveMarker.setAttribute("d", progressIndicatorDGroups.startPart +
                        (x + 2) +
                        progressIndicatorDGroups.secondPart +
                        (x + 2) +
                        progressIndicatorDGroups.lastPart);
            }
            if (x >=
                transitionConstraintsRef.current.minX + (playDataIndexRef.current + 1) * oneTickWidthInPx) {
                playDataIndexRef.current = Math.ceil(x / oneTickWidthInPx - 1);
                data.sorted[playDataIndexRef.current] &&
                    props.onSetInterval &&
                    props.onSetInterval({
                        from: data.minTimestamp,
                        to: data.sorted[playDataIndexRef.current].Timestamp
                    });
            }
            rafId.current = requestAnimationFrame(moveSlider);
        };
        rafId.current && cancelAnimationFrame(rafId.current);
        rafId.current = requestAnimationFrame(moveSlider);
    }, [
        data,
        props.mode,
        props.inPlayProgress,
        props.onSetInPlayProgress,
        props.onSetInterval,
        props.interval
    ]);
    const handlePause = useCallback(() => {
        var _a, _b, _c;
        if (!props.inPlayProgress)
            return;
        rafId.current && cancelAnimationFrame(rafId.current);
        setSliderMarkersShow(rangeSelectorRef, true);
        (_a = rangeSelectorRef.current) === null || _a === void 0 ? void 0 : _a.instance.setValue([(_b = props.interval) === null || _b === void 0 ? void 0 : _b.from, (_c = props.interval) === null || _c === void 0 ? void 0 : _c.to]);
        props.onSetInPlayProgress && props.onSetInPlayProgress(false);
        props.onSetPresentationState && props.onSetPresentationState("pause");
    }, [data, props.inPlayProgress, props.onSetInPlayProgress, props.onSetInterval, props.interval]);
    const handleStop = useCallback(() => {
        var _a;
        rafId.current && cancelAnimationFrame(rafId.current);
        const { rightSlider } = getSliderElements(rangeSelectorRef);
        if (rightSlider) {
            rightSlider.setAttribute("transform", `translate(${transitionConstraintsRef.current.maxX},${transitionConstraintsRef.current.y})`);
        }
        setSliderMarkersShow(rangeSelectorRef, true);
        props.onSetInterval && props.onSetInterval(undefined);
        (_a = rangeSelectorRef.current) === null || _a === void 0 ? void 0 : _a.instance.setValue([undefined, undefined]);
        props.onSetInPlayProgress && props.onSetInPlayProgress(false);
        props.onSetPresentationState && props.onSetPresentationState("stop");
    }, [data, props.inPlayProgress, props.onSetInPlayProgress, props.onSetInterval]);
    useEffect(() => {
        if (props.rangeSelectorRef && props.mode === TIMELINE_RANGE_SELECTOR_MODE.PRESENTATION) {
            props.rangeSelectorRef.current = {
                play: handlePlay,
                pause: handlePause,
                stop: handleStop
            };
        }
    }, [handlePlay, handlePause, handleStop, props.mode]);
    const handleRangeSelectorInitialized = useCallback((e) => {
        if (e.element) {
            defsRef.current = e.element.querySelector("defs");
            appendDefs(defsRef.current);
        }
    }, []);
    const handleIntervalChanged = useCallback((e) => {
        if (!props.onSetInterval || !e.event || !data)
            return;
        const from = isNumber(e.value[0]) ? e.value[0] : Number.parseInt(e.value[0]);
        const to = isNumber(e.value[1]) ? e.value[1] : Number.parseInt(e.value[1]);
        props.onSetInterval(!from || !to || (from === data.minTimestamp && to === data.maxTimestamp)
            ? undefined
            : { from, to });
    }, [props.onSetInterval, data === null || data === void 0 ? void 0 : data.minTimestamp, data === null || data === void 0 ? void 0 : data.maxTimestamp]);
    useEffect(() => {
        defsRef.current && changeColorDefs(defsRef.current, props.presentationState);
    }, [props.presentationState]);
    return (React.createElement("div", { className: classNames("overflow-hidden", props.className) },
        data && data.sorted.length > 0 && (React.createElement(Styled.RangeSelector, { mode: props.mode, inPlayProgress: props.inPlayProgress, canChangeInterval: !props.inPlayProgress, presentationState: props.presentationState, ref: rangeSelectorRef, dataSource: data.sorted, onInitialized: handleRangeSelectorInitialized, onValueChanged: !props.inPlayProgress ? handleIntervalChanged : undefined },
            React.createElement(Behavior, { snapToTicks: false, animationEnabled: false, allowSlidersSwap: props.mode === TIMELINE_RANGE_SELECTOR_MODE.TIMELINE, manualRangeSelectionEnabled: props.mode === TIMELINE_RANGE_SELECTOR_MODE.TIMELINE, moveSelectedRangeByClick: props.mode === TIMELINE_RANGE_SELECTOR_MODE.TIMELINE }),
            React.createElement(Margin, { left: 0, top: 10, right: 0, bottom: 12 }),
            React.createElement(Scale, { categories: timestampCategories, startValue: data.sortedMinTimestamp, endValue: data.sortedMaxTimestamp, minRange: data.aggregationInterval, aggregateByCategory: props.mode === TIMELINE_RANGE_SELECTOR_MODE.PRESENTATION, aggregationInterval: props.mode === TIMELINE_RANGE_SELECTOR_MODE.PRESENTATION
                    ? undefined
                    : data.aggregationInterval, discreteAxisDivisionMode: "betweenLabels", valueType: "numeric" },
                React.createElement(Label, { visible: true, alignment: "center", customizeText: props.mode === TIMELINE_RANGE_SELECTOR_MODE.PRESENTATION
                        ? customizeCountText
                        : customizeDateTimeText, backgroundColor: props.mode === TIMELINE_RANGE_SELECTOR_MODE.PRESENTATION
                        ? themeVariables["timeline-presentation-label-bg"]
                        : undefined },
                    React.createElement(Font, { color: themeVariables["timeline-presentation-label-color"], family: themeVariables["font-family-base"], size: themeVariables["font-size-small"], weight: 400 }))),
            React.createElement(Chart, null,
                React.createElement(Legend, { visible: false }),
                React.createElement(Series, { argumentField: "Timestamp", valueField: "NormalizedCount", type: "bar", color: themeVariables["timeline-presentation-bg"], showInLegend: false, ignoreEmptyPoints: true, cornerRadius: themeVariables["border-radius-small"] },
                    props.mode === TIMELINE_RANGE_SELECTOR_MODE.TIMELINE && (React.createElement(Aggregation, { enabled: true, method: "sum" })),
                    props.mode === TIMELINE_RANGE_SELECTOR_MODE.TIMELINE && (React.createElement(Label, { visible: true, alignment: "center", customizeText: customizeTotalLabelText, backgroundColor: themeVariables["brand-primary-100"], verticalOffset: 2, horizontalOffset: 0 },
                        React.createElement(Font, { color: themeVariables["gray-700"], family: themeVariables["font-family-base"], size: themeVariables["font-size-small"], weight: 400 }))))),
            React.createElement(SliderHandle, { color: themeVariables["timeline-strip-bg"], opacity: 1, width: 4 }),
            React.createElement(SliderMarker, { color: themeVariables["timeline-data-bg"], invalidRangeColor: themeVariables["brand-danger-400"], customizeText: customizeDateTimeText, paddingLeftRight: 8, paddingTopBottom: 2 },
                React.createElement(Font, { color: themeVariables["timeline-data-color"], family: themeVariables["font-family-base"], size: themeVariables["font-size-base"], weight: 400 })))),
        data && data.sorted.length === 0 && (React.createElement(WelcomePanel, { showIcon: false, message: locale.NO_DATA_TEXT, messageClassName: "font-h5" }))));
}
export default memo(TimelineRangeSelector);
TimelineRangeSelector.defaultProps = {
    maxTicksCount: 60
};
